import { X } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { PhotoPicker } from "@/components/PhotoPicker";
import { AiDescription } from "@/components/AdminAiDescription";

const input = "w-full px-3 py-2 rounded-xl border border-[#E7E0D3] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E06D44]/40";

export const SPEC_FIELDS = [
  ["materials", "Materials", "e.g. Natural rubber, nylon rope"],
  ["size", "Dimensions", "e.g. 7 in long, 2.5 in wide"],
  ["care", "Cleaning and care", "e.g. Rinse with warm soapy water, air dry"],
  ["dog_size", "Dog size guidance", "e.g. Medium dogs 20-50 lb (manufacturer)"],
];

export const isAiPhoto = (src = "") => /generated-images|r2\.dev/.test(src);

const money = (v) => (v === "" || v == null ? "" : v);

export const marginOf = (p) =>
  p.cost != null && p.cost !== "" && Number(p.price) > 0
    ? ((Number(p.price) - Number(p.cost) - Number(p.ship_cost || 0)) / Number(p.price)) * 100
    : null;

export const AdminProductDetails = ({ p, set }) => {
  const gallery = p.gallery || [];
  const margin = marginOf(p)?.toFixed(0) ?? null;
  return (
    <div data-testid={ADMIN.detailsPanel(p.slug)} className="col-span-full bg-[#FDFBF7] border border-[#F0EBE1] rounded-2xl p-4 grid sm:grid-cols-2 gap-4">
      <AiDescription p={p} set={set} />
      {SPEC_FIELDS.map(([k, label, ph]) => (
        <label key={k} className="text-sm">
          <span className="font-medium">{label}</span>
          <input data-testid={ADMIN.specInput(p.slug, k)} value={p[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} className={`${input} mt-1`} />
        </label>
      ))}
      <label className="text-sm">
        <span className="font-medium">Your cost per unit (private)</span>
        <input data-testid={ADMIN.costInput(p.slug)} type="number" step="0.01" min="0" value={money(p.cost)} onChange={(e) => set("cost", e.target.value === "" ? null : e.target.value)} placeholder="What you pay the supplier" className={`${input} mt-1`} />
      </label>
      <label className="text-sm">
        <span className="font-medium">Shipping cost per unit (private)</span>
        <input data-testid={ADMIN.shipCostInput(p.slug)} type="number" step="0.01" min="0" value={money(p.ship_cost)} onChange={(e) => set("ship_cost", e.target.value === "" ? null : e.target.value)} placeholder="Your average cost to ship one" className={`${input} mt-1`} />
      </label>
      <label className="text-sm">
        <span className="font-medium">Barcode / GTIN (for Google)</span>
        <input data-testid={`admin-gtin-input-${p.slug}`} inputMode="numeric" value={p.gtin || ""} onChange={(e) => set("gtin", e.target.value.replace(/\D/g, "").slice(0, 14))} placeholder="8, 12, 13 or 14 digits under the barcode" className={`${input} mt-1`} />
      </label>
      <label className="text-sm">
        <span className="font-medium">Manufacturer part number / MPN</span>
        <input data-testid={`admin-mpn-input-${p.slug}`} value={p.mpn || ""} onChange={(e) => set("mpn", e.target.value.slice(0, 70))} placeholder="Model or item number on the box" className={`${input} mt-1`} />
      </label>
      <p data-testid={ADMIN.marginPreview(p.slug)} className="sm:col-span-2 text-xs text-stone-500">
        {margin == null ? "Add your cost to see the gross margin on this product (before payment fees)." : `Gross margin before payment fees: ${margin}% of the $${Number(p.price).toFixed(2)} price.`}
      </p>
      <div className="sm:col-span-2">
        <p className="text-sm font-medium">More photos (e.g. Kiddee playing with it)</p>
        <div className="mt-2 flex flex-wrap gap-3">
          {gallery.map((src, i) => (
            <div key={`${src}-${i}`} className="relative">
              <img src={imgUrl(src)} alt={`${p.name} extra ${i + 1}`} className="w-16 h-16 rounded-xl object-cover" />
              <button type="button" aria-label="Remove photo" data-testid={ADMIN.galleryRemove(p.slug, i)} onClick={() => set("gallery", gallery.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#1C1917] text-white grid place-items-center"><X size={12} /></button>
            </div>
          ))}
          {gallery.length < 8 && (
            <PhotoPicker value="" onChange={(url) => set("gallery", [...gallery, url])} testId={ADMIN.galleryAdd(p.slug)} label={`Add a photo for ${p.name}`} />
          )}
        </div>
      </div>
    </div>
  );
};
